import { Renderer2 } from '@angular/core';

export interface TabsetA11yOptions {
  /** Prefijo único para ids (p. ej. "profile", "cases") cuando falten. */
  idPrefix: string;
  /** Nombre accesible del tablist. */
  tablistLabel: string;
}

/**
 * Corrige roles ARIA del tabset de ngx-bootstrap para Lighthouse/axe:
 *
 * Problemas del markup por defecto:
 * 1. <ul role="tablist"> > <li> (role implícito listitem) — inválido; hijos de
 *    tablist deben ser tab o presentation/none.
 * 2. Si el <tab> no tiene id, aria-controls="" y aria-labelledby="" — inválidos.
 * 3. tablist sin nombre accesible.
 *
 * Preferible: declarar [id] en cada <tab> (ngx enlaza link/panel solo).
 * Esta utilidad completa lo que falte y neutraliza los <li>.
 *
 * @returns true si se aplicó el parche (o ya estaba aplicado)
 */
export function patchNgxBootstrapTabsetA11y(
  root: ParentNode,
  renderer: Renderer2,
  options: TabsetA11yOptions
): boolean {
  const tabList = root.querySelector('.nav.nav-tabs, ul.nav[role="tablist"]') as HTMLElement | null;
  if (!tabList) {
    return false;
  }

  if (tabList.getAttribute('data-a11y-patched') === 'true') {
    return true;
  }

  renderer.setAttribute(tabList, 'role', 'tablist');
  if (options.tablistLabel) {
    renderer.setAttribute(tabList, 'aria-label', options.tablistLabel);
  }

  // listitem dentro de tablist → presentation only
  tabList.querySelectorAll('.nav-item').forEach((item) => {
    renderer.setAttribute(item as Element, 'role', 'none');
  });

  const tabsetHost = tabList.parentElement;
  const tabContent =
    (tabsetHost && tabsetHost.querySelector('.tab-content')) || root.querySelector('.tab-content');
  const tabs = tabList.querySelectorAll('.nav-link[role="tab"]');
  const panels = tabContent ? tabContent.querySelectorAll('.tab-pane') : [];

  tabs.forEach((tab, index) => {
    const panel = panels[index] as HTMLElement | undefined;
    const existingControls = tab.getAttribute('aria-controls');
    const hasValidControls = !!existingControls && existingControls.trim().length > 0;

    if (hasValidControls && panel) {
      // ngx ya enlazó via [id] del <tab>; asegurar role en panel
      renderer.setAttribute(panel, 'role', 'tabpanel');
      if (!panel.getAttribute('aria-labelledby') && tab.id) {
        renderer.setAttribute(panel, 'aria-labelledby', tab.id);
      }
      return;
    }

    const tabId = tab.id && tab.id.trim() ? tab.id : `${options.idPrefix}-tab-${index}`;
    const panelId =
      (panel && panel.id && panel.id.trim()) || `${options.idPrefix}-tabpanel-${index}`;

    renderer.setAttribute(tab as Element, 'id', tabId);

    if (panel) {
      renderer.setAttribute(tab as Element, 'aria-controls', panelId);
      renderer.setAttribute(panel, 'id', panelId);
      renderer.setAttribute(panel, 'role', 'tabpanel');
      renderer.setAttribute(panel, 'aria-labelledby', tabId);
    } else {
      renderer.removeAttribute(tab as Element, 'aria-controls');
    }
  });

  // Limpia aria-controls vacíos residuales
  tabList.querySelectorAll('.nav-link[aria-controls=""]').forEach((tab) => {
    renderer.removeAttribute(tab as Element, 'aria-controls');
  });

  renderer.setAttribute(tabList, 'data-a11y-patched', 'true');
  return true;
}
